/**
 * Materiales de montaje (antes "Mi Logística") — Pantalla operativa de recepción.
 *
 * Tabs: 🏭 Esperando en Nave · 🏬 Esperando en PDV · Ya en Nave · Ya en PDV · 📤 Mensajería.
 * Vista por defecto: LISTADO compacto (estilo Solicitudes de trabajo). Toggle Listado/Fichas
 * persistido en localStorage. Botón "✅ Recibido" → mini-form (fecha real, nº bulto, bultos
 * totales, nota). En Nave dispara la etiqueta automática (1/N…N/N) y abre el PDF.
 *
 * Visible para: roles "logistica", "administracion", "admin" (+ permiso logistica:recepcion).
 */
import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import ProyectoThumb from "@/components/ProyectoThumb";
import BuscadorLineas from "@/components/BuscadorLineas";
import { ListadoPaquetes } from "@/components/logistica/ListadoPaquetes";
import {
  Truck, Building2, Loader2, CheckCircle2, AlertTriangle, Clock,
  MapPin, Calendar, Package, Tag, Printer, ArrowUpDown, Layers, Ruler, Factory, ArrowLeft,
  List, LayoutGrid,
} from "lucide-react";

const VISTA_KEY = "dnf-logistica-vista";

const TABS = [
  { key: "nave", label: "🏭 Esperando en Nave", short: "Nave", color: "blue" },
  { key: "pdv",  label: "🏬 Esperando en PDV",  short: "PDV",  color: "violet" },
  // Iter 84.5 — Material ya recibido pero todavía sin montar (visibilidad para Sara)
  { key: "en_nave", label: "📦 Ya en Nave sin montar", short: "En Nave", color: "emerald" },
  { key: "en_pdv",  label: "🏪 Ya en PDV sin montar",  short: "En PDV",  color: "amber" },
  { key: "mensajeria", label: "📤 Mensajería",  short: "Msj",  color: "fuchsia" },
];

const AGENCIAS = ["GLS", "Tipsa", "Seur", "MRW", "Correos Express", "Nacex", "Otro"];

export default function MiLogistica() {
  const { hasRole } = useAuth();
  const nav = useNavigate();
  // Modo recepción: usuarios (p.ej. montadores) que entran solo con el permiso `logistica:recepcion`
  const modoRecepcion = !hasRole(["admin", "logistica", "administracion"]);
  const tabs = modoRecepcion ? TABS.filter((t) => t.key === "nave" || t.key === "pdv") : TABS;
  const [data, setData] = useState({ nave: [], pdv: [], en_nave: [], en_pdv: [], kpis: {} });
  const [mensajeria, setMensajeria] = useState({ pendiente_llamar: [], esperando_recogida: [], kpis: {} });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("nave");
  const [busqueda, setBusqueda] = useState("");
  const [filterCliente, setFilterCliente] = useState("__all__");
  const [filterOrigen, setFilterOrigen] = useState("__all__");
  const [orden, setOrden] = useState("antiguedad");  // antiguedad | objetivo | cliente | peticion
  const [groupByPet, setGroupByPet] = useState(false);
  const [recibiendo, setRecibiendo] = useState(null);
  const [llamando, setLlamando] = useState(null);  // línea para registrar llamada agencia
  // Jun-2026 (Elia) — vista Listado (default) / Fichas, persistida
  const [vista, setVista] = useState(() => localStorage.getItem(VISTA_KEY) === "fichas" ? "fichas" : "listado");
  useEffect(() => { localStorage.setItem(VISTA_KEY, vista); }, [vista]);
  const [highlightId, setHighlightId] = useState(null);

  // Buscador transversal → localizar el componente en su tab, filtrar por código y resaltar
  const localizar = (hit, comp) => {
    const codigo = hit.peticion_codigo || "";
    const compIds = comp ? [comp.id] : (hit.componentes || []).map((c) => c.id);
    const tabsBusq = tabs.filter((t) => t.key !== "mensajeria").map((t) => t.key);
    let tabDestino = null, idDestino = null;
    for (const k of tabsBusq) {
      const found = (data[k] || []).find((c) => compIds.includes(c.id) || c.linea?.id === hit.linea_id);
      if (found) { tabDestino = k; idDestino = found.id; break; }
    }
    setBusqueda(codigo);
    setFilterCliente("__all__");
    setFilterOrigen("__all__");
    if (!tabDestino) {
      toast.info(`${codigo}: ese material no está pendiente en Materiales de montaje (ya montado o fuera de logística).`);
      setHighlightId(null);
      return;
    }
    setTab(tabDestino);
    setHighlightId(idDestino);
    setTimeout(() => {
      document.getElementById(`paquete-${idDestino}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
  };

  const cargar = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/logistica/entrada"),
        modoRecepcion ? Promise.resolve(null) : api.get("/logistica/mensajeria"),
      ]);
      setData(a.data);
      if (b) setMensajeria(b.data);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [modoRecepcion]);

  useEffect(() => { cargar(); }, [cargar]);

  // Universo crudo del tab
  const universo = data[tab] || [];

  // Opciones únicas para los selects (basadas en el tab actual)
  const clientesUnicos = React.useMemo(() => {
    const map = new Map();
    universo.forEach((c) => {
      const n = c.cliente?.nombre;
      if (n) map.set(n, n);
    });
    return Array.from(map.values()).sort();
  }, [universo]);
  const origenesUnicos = React.useMemo(() => {
    const set = new Set();
    universo.forEach((c) => { if (c.origen) set.add(c.origen); });
    return Array.from(set).sort();
  }, [universo]);

  const items = React.useMemo(() => {
    let arr = universo.filter((c) => {
      if (busqueda) {
        const t = `${c.peticion?.codigo || ""} ${c.nombre || ""} ${c.cliente?.nombre || ""} ${c.espacio?.nombre || ""} ${c.espacio?.ciudad || ""} ${c.peticion?.marca || ""} ${c.referencia_cliente || ""}`.toLowerCase();
        if (!t.includes(busqueda.toLowerCase())) return false;
      }
      if (filterCliente !== "__all__" && c.cliente?.nombre !== filterCliente) return false;
      if (filterOrigen !== "__all__" && c.origen !== filterOrigen) return false;
      return true;
    });
    // Orden
    arr = [...arr];
    if (orden === "antiguedad") {
      arr.sort((a, b) => (b.dias_en_columna || 0) - (a.dias_en_columna || 0));
    } else if (orden === "objetivo") {
      arr.sort((a, b) => (a.peticion?.dias_a_objetivo ?? 9999) - (b.peticion?.dias_a_objetivo ?? 9999));
    } else if (orden === "cliente") {
      arr.sort((a, b) => (a.cliente?.nombre || "").localeCompare(b.cliente?.nombre || ""));
    } else if (orden === "peticion") {
      arr.sort((a, b) => (a.peticion?.codigo || "").localeCompare(b.peticion?.codigo || ""));
    }
    return arr;
  }, [universo, busqueda, filterCliente, filterOrigen, orden]);

  // Agrupar por petición si está activo
  const grupos = React.useMemo(() => {
    if (!groupByPet) return null;
    const map = new Map();
    items.forEach((c) => {
      const k = c.peticion?.codigo || "(sin código)";
      if (!map.has(k)) map.set(k, { codigo: k, peticion: c.peticion, cliente: c.cliente, items: [] });
      map.get(k).items.push(c);
    });
    return Array.from(map.values());
  }, [items, groupByPet]);

  // Conteo por tab para el badge
  const conteoTab = (k) => {
    if (k === "mensajeria") return (mensajeria.pendiente_llamar?.length || 0) + (mensajeria.esperando_recogida?.length || 0);
    return (data[k] || []).length;
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
    </div>
  );

  return (
    <div className="p-3 sm:p-4 max-w-[1600px] mx-auto" data-testid="mi-logistica-page" data-modo={modoRecepcion ? "recepcion" : "completo"}>
      <PageHeader
        icon={Truck}
        title={modoRecepcion ? "📦 Recepción de material" : "Materiales de montaje"}
        subtitle={modoRecepcion ? "Marca el material que llega e imprime su etiqueta" : "Pendientes de recibir en nave o entregar en PDV"}
        actions={modoRecepcion && hasRole("montador") ? (
          <Button variant="outline" size="sm" onClick={() => nav("/mi-ruta")} data-testid="volver-mi-ruta"
            className="bg-transparent border-zinc-700 text-zinc-300 h-10 sm:h-9">
            <ArrowLeft className="w-4 h-4 mr-1" /> Mi Ruta
          </Button>
        ) : null}
      />

      {/* KPIs Sara */}
      <div className={`mt-4 grid gap-2 ${modoRecepcion ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6"}`} data-testid="kpis-sara">
        <KpiSara label="En Nave" value={data.kpis.total_nave} icon={Building2} color="text-blue-300" testid="kpi-nave" />
        <KpiSara label="En PDV" value={data.kpis.total_pdv} icon={MapPin} color="text-violet-300" testid="kpi-pdv" />
        {!modoRecepcion && <>
        {/* Iter 84.5 — Material recibido pendiente de montar */}
        <KpiSara label="Ya en Nave" value={data.kpis.total_en_nave} icon={Package} color="text-emerald-300" testid="kpi-en-nave"
                 tip="Recibido en nave, pendiente de salir a PDV o montar" />
        <KpiSara label="Ya en PDV" value={data.kpis.total_en_pdv} icon={MapPin} color="text-amber-300" testid="kpi-en-pdv"
                 tip="Entregado en PDV, pendiente de montar" />
        <KpiSara label="Nave retraso" value={data.kpis.retrasados_nave_7d}
                 icon={AlertTriangle} color={data.kpis.retrasados_nave_7d > 0 ? "text-red-300" : "text-zinc-500"}
                 testid="kpi-nave-retr"
                 tip="Llevan >7d sin recibirse"
                 warn={data.kpis.retrasados_nave_7d > 0} />
        <KpiSara label="PDV retraso" value={data.kpis.retrasados_pdv_7d}
                 icon={AlertTriangle} color={data.kpis.retrasados_pdv_7d > 0 ? "text-red-300" : "text-zinc-500"}
                 testid="kpi-pdv-retr"
                 tip="Llevan >7d sin entregarse"
                 warn={data.kpis.retrasados_pdv_7d > 0} />
        </>}
      </div>

      {/* Tabs (scroll horizontal en móvil) + filtros (apilados en móvil) */}
      <div className="mt-4 sm:mt-6">
        <BuscadorLineas
          testId="buscador-materiales"
          onSelect={localizar}
          placeholder="Buscar material: código DNF, cliente, PDV, ciudad, marca, ref. cliente, componente…"
          hint="pulsa un resultado para localizarlo en el listado" />
      </div>
      <div className="border-b border-zinc-800 flex flex-col lg:flex-row lg:items-center gap-2">
        <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0 [scrollbar-width:none]">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`shrink-0 px-3 sm:px-4 py-2.5 sm:py-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
              tab === t.key
                ? `border-amber-500 text-amber-300`
                : "border-transparent text-zinc-500 hover:text-zinc-300"
            }`}>
            <span className="sm:hidden">{t.label.split(" ")[0]} {t.short}</span>
            <span className="hidden sm:inline">{t.label}</span>
            <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400 font-mono">
              {conteoTab(t.key)}
            </span>
          </button>
        ))}
        </div>
        <div className="lg:ml-auto pb-2 lg:pb-1 flex flex-wrap items-center gap-2">
          <Input placeholder="Buscar PDV, cliente, código, ref. cliente…" value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            data-testid="busqueda-logistica"
            className="h-10 sm:h-8 w-full sm:w-56 bg-zinc-900 border-zinc-800 text-sm sm:text-xs" />
          <Select value={filterCliente} onValueChange={setFilterCliente}>
            <SelectTrigger data-testid="filter-cliente"
              className="h-10 sm:h-8 flex-1 sm:flex-none sm:w-44 bg-zinc-900 border-zinc-800 text-xs">
              <SelectValue placeholder="Cliente" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
              <SelectItem value="__all__">Todos los clientes</SelectItem>
              {clientesUnicos.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterOrigen} onValueChange={setFilterOrigen}>
            <SelectTrigger data-testid="filter-origen"
              className="h-10 sm:h-8 flex-1 sm:flex-none sm:w-40 bg-zinc-900 border-zinc-800 text-xs">
              <SelectValue placeholder="Origen" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
              <SelectItem value="__all__">Cualquier origen</SelectItem>
              {origenesUnicos.map((o) => <SelectItem key={o} value={o}>{o.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={orden} onValueChange={setOrden}>
            <SelectTrigger data-testid="filter-orden"
              className="h-10 sm:h-8 flex-1 sm:flex-none sm:w-36 bg-zinc-900 border-zinc-800 text-xs">
              <ArrowUpDown className="w-3 h-3 mr-1" /><SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
              <SelectItem value="antiguedad">Antigüedad</SelectItem>
              <SelectItem value="objetivo">Fecha objetivo</SelectItem>
              <SelectItem value="cliente">Cliente</SelectItem>
              <SelectItem value="peticion">Petición</SelectItem>
            </SelectContent>
          </Select>
          <button type="button" onClick={() => setGroupByPet((v) => !v)}
            data-testid="toggle-group-peticion"
            title="Agrupar por petición"
            className={`h-10 sm:h-8 px-3 sm:px-2 rounded border text-xs inline-flex items-center gap-1
              ${groupByPet ? "bg-amber-500/20 border-amber-500/50 text-amber-200" : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200"}`}>
            <Layers className="w-3.5 h-3.5" />
            {groupByPet ? "Agrupado" : "Agrupar"}
          </button>
          <div className="h-10 sm:h-8 inline-flex rounded border border-zinc-800 overflow-hidden" data-testid="toggle-vista" role="group" aria-label="Vista">
            <button type="button" onClick={() => setVista("listado")} data-testid="vista-listado" title="Vista listado compacto"
              aria-pressed={vista === "listado"}
              className={`px-3 sm:px-2 text-xs inline-flex items-center gap-1 ${vista === "listado" ? "bg-amber-500/20 text-amber-200" : "bg-zinc-900 text-zinc-400 hover:text-zinc-200"}`}>
              <List className="w-3.5 h-3.5" /> Listado
            </button>
            <button type="button" onClick={() => setVista("fichas")} data-testid="vista-fichas" title="Vista de fichas"
              aria-pressed={vista === "fichas"}
              className={`px-3 sm:px-2 text-xs inline-flex items-center gap-1 border-l border-zinc-800 ${vista === "fichas" ? "bg-amber-500/20 text-amber-200" : "bg-zinc-900 text-zinc-400 hover:text-zinc-200"}`}>
              <LayoutGrid className="w-3.5 h-3.5" /> Fichas
            </button>
          </div>
        </div>
      </div>

      {/* Indicador resultados filtrados */}
      {(busqueda || filterCliente !== "__all__" || filterOrigen !== "__all__") && tab !== "mensajeria" && (
        <div className="mt-2 text-[11px] text-zinc-500">
          Mostrando <span className="text-amber-300 font-semibold">{items.length}</span> de {universo.length} paquetes
          {(filterCliente !== "__all__" || filterOrigen !== "__all__" || busqueda) && (
            <button onClick={() => { setBusqueda(""); setFilterCliente("__all__"); setFilterOrigen("__all__"); setHighlightId(null); }}
              className="ml-2 underline hover:text-amber-300">Limpiar filtros</button>
          )}
        </div>
      )}

      {/* Listado */}
      {tab === "mensajeria" ? (
        <TabMensajeria data={mensajeria} onLlamar={setLlamando} onRecargar={cargar} />
      ) : groupByPet ? (
        <div className="mt-4 space-y-3" data-testid="grupos-logistica">
          {grupos && grupos.length === 0 ? (
            <div className="col-span-full text-center py-12 text-zinc-500">
              <Package className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <div className="text-sm">No hay paquetes esperando</div>
            </div>
          ) : grupos && grupos.map((g) => (
            <div key={g.codigo} className="bg-zinc-900/30 border border-zinc-800 rounded-lg p-3"
              data-testid={`grupo-${g.codigo}`}>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div>
                  <span className="text-amber-300 font-semibold text-sm">{g.codigo}</span>
                  <span className="text-zinc-400 text-xs ml-2">{g.peticion?.nombre || "—"}</span>
                  <span className="text-zinc-600 text-xs ml-2">· {g.cliente?.nombre || "—"}</span>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-200">
                  {g.items.length} {g.items.length === 1 ? "paquete" : "paquetes"}
                </span>
              </div>
              {vista === "listado" ? (
                <ListadoPaquetes items={g.items} tab={tab} onRecibir={setRecibiendo} highlightId={highlightId} />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {g.items.map((c) => (
                    <TarjetaPaquete key={c.id} c={c} tab={tab} onRecibir={() => setRecibiendo(c)} highlighted={highlightId === c.id} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : vista === "listado" ? (
        <div className="mt-4">
          <ListadoPaquetes items={items} tab={tab} onRecibir={setRecibiendo}
            orden={orden} setOrden={setOrden} highlightId={highlightId}
            emptyText={`No hay paquetes esperando en ${tab === "nave" ? "nave" : tab === "pdv" ? "PDV" : "esta pestaña"}`} />
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="grid-logistica">
          {items.length === 0 ? (
            <div className="col-span-full text-center py-12 text-zinc-500">
              <Package className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <div className="text-sm">No hay paquetes esperando en {tab === "nave" ? "nave" : "PDV"}</div>
            </div>
          ) : items.map((c) => (
            <TarjetaPaquete key={c.id} c={c} tab={tab} onRecibir={() => setRecibiendo(c)} highlighted={highlightId === c.id} />
          ))}
        </div>
      )}

      {recibiendo && (
        <DialogRecibir
          componente={recibiendo}
          destino={tab}
          onClose={() => setRecibiendo(null)}
          onDone={() => { setRecibiendo(null); cargar(); }}
        />
      )}

      {llamando && (
        <DialogLlamarAgencia
          linea={llamando}
          onClose={() => setLlamando(null)}
          onDone={() => { setLlamando(null); cargar(); }}
        />
      )}
    </div>
  );
}

const KpiSara = ({ label, value, icon: Icon, color, tip, testid, warn }) => (
  <div className={`bg-zinc-900/40 border ${warn ? "border-red-500/40" : "border-zinc-800"} rounded-lg p-3`}
       title={tip} data-testid={testid}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-[10px] text-zinc-500 uppercase tracking-wide">{label}</span>
      <Icon className={`w-3.5 h-3.5 ${color}`} />
    </div>
    <div className={`text-2xl font-bold ${color} font-mono`}>{value ?? 0}</div>
  </div>
);

function TarjetaPaquete({ c, tab, onRecibir, highlighted }) {
  const dias = c.dias_en_columna ?? 0;
  const retr = dias >= 7;
  const vencido = c.peticion?.dias_a_objetivo !== null && c.peticion?.dias_a_objetivo < 0;
  const imagen = c.imagen_url || c.peticion?.imagen_url;
  const medidas = c.medidas
    ? `${c.medidas}${c.m2 ? ` · ${c.m2} m²` : ""}`
    : (c.m2 ? `${c.m2} m²` : null);
  return (
    <div id={`paquete-${c.id}`} className={`bg-zinc-900/40 border rounded-lg p-3 hover:border-zinc-700 transition-all
                     ${highlighted ? "border-cyan-400 ring-2 ring-cyan-500/40" : retr ? "border-red-500/40" : vencido ? "border-amber-500/40" : "border-zinc-800"}`}
         data-testid={`paquete-${c.id}`} data-highlight={highlighted ? "1" : undefined}>
      {/* Jun-2026 — imagen grande clicable si el componente/petición la tiene */}
      {imagen && (
        <button onClick={() => window.open(imagen, "_blank", "noopener,noreferrer")}
          data-testid={`paquete-imagen-${c.id}`}
          title="Ver imagen a tamaño completo"
          className="block w-full h-24 mb-2 rounded-md overflow-hidden border border-zinc-800 hover:border-cyan-500/50 transition-colors">
          <img src={imagen} alt={c.nombre || "material"} className="w-full h-full object-cover" loading="lazy" />
        </button>
      )}
      <div className="flex items-start justify-between gap-2 mb-2">
        {!imagen && (
          <ProyectoThumb url={c.peticion?.imagen_url} nombre={c.cliente?.nombre || "?"} size={36} rounded="lg"
            data-testid={`paquete-thumb-${c.id}`} />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] text-amber-400 font-mono">{c.peticion?.codigo}</span>
            {c.peticion?.marca && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300">
                <Tag className="w-2 h-2 inline" /> {c.peticion.marca}
              </span>
            )}
            <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${
              retr ? "bg-red-500/30 text-red-200" :
              dias >= 3 ? "bg-amber-500/20 text-amber-200" : "bg-zinc-800 text-zinc-400"
            }`} title={`Esperando desde hace ${dias} días`}>
              <Clock className="w-2 h-2 inline" /> {dias}d
            </span>
          </div>
          <div className="text-sm font-semibold text-zinc-100 mt-1 line-clamp-2">{c.nombre || "—"}</div>
        </div>
      </div>

      {/* Jun-2026 — chips con la info física del material */}
      <div className="flex items-center gap-1.5 flex-wrap mt-1" data-testid={`paquete-info-material-${c.id}`}>
        {medidas && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-300 border border-cyan-500/25 inline-flex items-center gap-1">
            <Ruler className="w-2.5 h-2.5" /> {medidas}
          </span>
        )}
        {(c.cantidad || 1) > 1 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 border border-zinc-700 inline-flex items-center gap-1">
            <Layers className="w-2.5 h-2.5" /> ×{c.cantidad}
          </span>
        )}
        {c.material?.nombre && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/25"
            title={c.material.categoria ? `Categoría: ${c.material.categoria}` : ""}>
            {c.material.nombre}
          </span>
        )}
        {c.proveedor?.nombre && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-300 border border-orange-500/25 inline-flex items-center gap-1"
            title={c.proveedor.telefono ? `Tel: ${c.proveedor.telefono}` : ""}>
            <Factory className="w-2.5 h-2.5" /> {c.proveedor.nombre}
          </span>
        )}
      </div>

      {(c.descripcion || c.descripcion_encargo || c.notas || c.referencia_cliente) && (
        <div className="mt-1.5 space-y-0.5 text-[10px]" data-testid={`paquete-detalles-${c.id}`}>
          {c.descripcion && <div className="text-zinc-400 line-clamp-2">{c.descripcion}</div>}
          {c.descripcion_encargo && (
            <div className="text-amber-200/90 bg-amber-500/10 border border-amber-500/20 rounded px-1.5 py-0.5 line-clamp-2"
              title={c.descripcion_encargo}>
              🚚 {c.descripcion_encargo}
            </div>
          )}
          {c.notas && <div className="text-zinc-500 italic line-clamp-2" title={c.notas}>📝 {c.notas}</div>}
          {c.referencia_cliente && <div className="text-zinc-500">Ref. cliente: <span className="text-zinc-300 font-mono">{c.referencia_cliente}</span></div>}
        </div>
      )}

      <div className="space-y-0.5 text-[11px] text-zinc-400 mt-2">
        <div className="flex items-center gap-1">
          <Building2 className="w-3 h-3 text-zinc-500" /> {c.cliente?.nombre || "—"}
        </div>
        <div className="flex items-center gap-1">
          <MapPin className="w-3 h-3 text-zinc-500" /> {c.espacio?.nombre || "—"} · {c.espacio?.ciudad || "—"}
        </div>
        {c.peticion?.fecha_objetivo && (
          <div className={`flex items-center gap-1 ${vencido ? "text-red-300" : "text-zinc-400"}`}>
            <Calendar className="w-3 h-3" /> Objetivo: {new Date(String(c.peticion.fecha_objetivo).slice(0, 10) + "T00:00:00").toLocaleDateString("es-ES")}
            {vencido && <span className="text-[9px] bg-red-500/30 text-red-200 px-1 rounded font-bold ml-1">VENCIDO</span>}
          </div>
        )}
        <div className="text-[10px] text-zinc-600 pt-1">
          Origen: {c.origen?.replace(/_/g, " ") || "—"}
          {c.bultos_recepcionados ? <span className="text-zinc-400 ml-2">📦 {c.bultos_recepcionados} bulto{c.bultos_recepcionados !== 1 ? "s" : ""}</span> : null}
        </div>
      </div>

      <Button onClick={onRecibir} size="sm"
        data-testid={`btn-recibir-${c.id}`}
        disabled={tab === "en_nave" || tab === "en_pdv"}
        className={`mt-3 w-full font-semibold h-11 sm:h-9 text-sm ${
          tab === "en_nave" || tab === "en_pdv"
            ? "bg-zinc-800 text-zinc-500 cursor-default"
            : "bg-emerald-500 hover:bg-emerald-400 text-zinc-950"
        }`}>
        <CheckCircle2 className="w-4 h-4 mr-1.5" />
        {tab === "nave" ? "Recibido en nave"
          : tab === "pdv" ? "Entregado en PDV"
          : tab === "en_nave" ? "Esperando salir a PDV o montaje"
          : "Esperando montaje en PDV"}
      </Button>
    </div>
  );
}

function DialogRecibir({ componente, destino, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [fecha, setFecha] = useState(today);
  const [bulto, setBulto] = useState("");
  const [bultosTotales, setBultosTotales] = useState("");  // Iter 60.5
  const [nota, setNota] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const body = {
        destino,
        fecha_recepcion: fecha,
        bulto_numero: bulto ? Number(bulto) : null,
        bultos_recepcionados: bultosTotales ? Number(bultosTotales) : null,
        nota: nota || null,
      };
      const { data } = await api.post(`/logistica/recepcionar/${componente.id}`, body);
      // Toast con acción "Deshacer" durante 12s (Jun 2026 — Fernando)
      toast.success(`Componente recibido (${data.estado_nuevo})`, {
        duration: 12000,
        action: {
          label: "↩️ Deshacer",
          onClick: async () => {
            try {
              await api.post(`/logistica/revertir-recepcion/${componente.id}`);
              toast.success("Recepción revertida — el componente vuelve a 'pendiente proveedor'");
              onDone();
            } catch (err) {
              toast.error("No se pudo revertir: " + (err?.response?.data?.detail || err.message));
            }
          },
        },
      });
      // Si es nave, descargar la etiqueta (generada en el cambio de estado)
      if (destino === "nave") {
        try {
          // Iter 78 — si el usuario indicó >1 bultos totales, pedimos todas las
          // etiquetas (1/N, 2/N, …) en un solo PDF.
          const totalNum = bultosTotales ? Number(bultosTotales) : 1;
          const url_params = totalNum > 1 ? "?all_bultos=true" : "";
          const r = await api.get(`/componentes/${componente.id}/etiqueta.pdf${url_params}`, { responseType: "blob" });
          const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
          const win = window.open(url, "_blank");
          if (!win) {
            // Safari/Chrome móvil bloquean popups tras un await → ofrecemos abrirla a mano
            toast("Etiqueta lista", {
              description: "Tu navegador bloqueó la ventana. Pulsa para abrirla.",
              duration: 60000,
              action: { label: "🖨️ Abrir etiqueta", onClick: () => window.open(url, "_blank") },
            });
            setTimeout(() => URL.revokeObjectURL(url), 120000);
          } else {
            setTimeout(() => URL.revokeObjectURL(url), 30000);
          }
          if (totalNum > 1) {
            toast.success(`PDF con ${totalNum} etiquetas generado (1/${totalNum} … ${totalNum}/${totalNum})`, { duration: 6000 });
          }
        } catch (e) {
          toast.error("No se pudo generar la etiqueta automáticamente — imprímela desde el componente.");
        }
      }
      onDone();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md w-[calc(100%-1.5rem)] sm:w-full max-h-[92vh] overflow-y-auto" data-testid="dialog-recibir">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            Recepción · {destino === "nave" ? "🏭 NAVE" : "🏬 PDV"}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-400">
            Confirma la fecha de recepción, anota el nº de bulto y cualquier nota.
            {destino === "nave" && " Se generará la etiqueta Brother automáticamente."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2 text-xs">
            <div className="font-semibold text-zinc-100">{componente.nombre}</div>
            <div className="text-zinc-500 mt-0.5">
              {componente.peticion?.codigo} · {componente.cliente?.nombre}
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-400">Fecha de recepción</label>
            <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)}
              data-testid="recibir-fecha"
              className="bg-zinc-900 border-zinc-800 text-zinc-100" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-zinc-400">
                Nº bulto {destino === "nave" && <span className="text-zinc-600">(en etiqueta)</span>}
              </label>
              <Input type="number" value={bulto} onChange={(e) => setBulto(e.target.value)}
                placeholder="1, 2, 3…"
                data-testid="recibir-bulto"
                className="bg-zinc-900 border-zinc-800 text-zinc-100" />
            </div>
            <div>
              <label className="text-xs text-zinc-400">
                Bultos totales <span className="text-zinc-600">(cuántos llegaron)</span>
              </label>
              <Input type="number" value={bultosTotales} onChange={(e) => setBultosTotales(e.target.value)}
                placeholder="Total recibidos"
                data-testid="recibir-bultos-totales"
                className="bg-zinc-900 border-zinc-800 text-zinc-100" />
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-400">Nota (opcional)</label>
            <Textarea value={nota} onChange={(e) => setNota(e.target.value)}
              data-testid="recibir-nota"
              placeholder="Ej.: Caja golpeada en una esquina, pero contenido OK"
              rows={2}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 text-xs" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting} className="h-11 sm:h-9">Cancelar</Button>
          <Button onClick={submit} disabled={submitting}
            data-testid="recibir-confirmar"
            className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold h-11 sm:h-9">
            {submitting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Guardando…</> :
              destino === "nave" ? <><Printer className="w-4 h-4 mr-1.5" /> Recibir + Imprimir etiqueta</> :
              <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Marcar entregado</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ TAB MENSAJERÍA (Caso 2: Producción + Envío) ============
function TabMensajeria({ data, onLlamar, onRecargar }) {
  const pend = data.pendiente_llamar || [];
  const esp = data.esperando_recogida || [];

  const marcarRecogido = async (lid) => {
    if (!window.confirm("¿Confirmar que la agencia ha recogido el paquete? Esto generará el albarán automático.")) return;
    try {
      await api.post(`/lineas/${lid}/marcar-envio-recogido`);
      toast.success("Recogido · albarán generado");
      onRecargar();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    }
  };

  const imprimirEtiqueta = async (lid) => {
    try {
      const r = await api.get(`/lineas/${lid}/etiqueta-envio.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error("No se pudo generar la etiqueta");
    }
  };

  return (
    <div className="mt-4 space-y-6" data-testid="tab-mensajeria-content">
      {/* SECCIÓN 1: Pendiente llamar */}
      <section>
        <h3 className="text-xs font-bold uppercase tracking-wider text-fuchsia-300 mb-2 flex items-center gap-2">
          📞 Pendiente llamar a la agencia
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-500/15 text-fuchsia-300 font-mono">{pend.length}</span>
        </h3>
        {pend.length === 0 ? (
          <div className="text-xs text-zinc-500 italic py-3">Nada pendiente · todo llamado o sin envíos producidos.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="grid-pendiente-llamar">
            {pend.map((l) => (
              <TarjetaMensajeria key={l.linea_id} l={l} fase="pendiente" onAccion={() => onLlamar(l)} onImprimir={imprimirEtiqueta} />
            ))}
          </div>
        )}
      </section>

      {/* SECCIÓN 2: Esperando recogida */}
      <section>
        <h3 className="text-xs font-bold uppercase tracking-wider text-amber-300 mb-2 flex items-center gap-2">
          🚚 Esperando recogida por la agencia
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 font-mono">{esp.length}</span>
        </h3>
        {esp.length === 0 ? (
          <div className="text-xs text-zinc-500 italic py-3">Nada esperando recogida.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="grid-esperando-recogida">
            {esp.map((l) => (
              <TarjetaMensajeria key={l.linea_id} l={l} fase="esperando"
                                 onAccion={() => marcarRecogido(l.linea_id)}
                                 onImprimir={imprimirEtiqueta} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function TarjetaMensajeria({ l, fase, onAccion, onImprimir }) {
  const dias = l.dias_listo ?? 0;
  const urgente = dias >= 3;
  return (
    <div className={`bg-zinc-900/40 border rounded-lg p-3 transition-all
                     ${urgente ? "border-amber-500/40" : "border-zinc-800"}`}
         data-testid={`mensajeria-${l.linea_id}`}>
      <div className="flex items-center gap-1.5 flex-wrap mb-2">
        <ProyectoThumb url={l.imagen_url || l.peticion?.imagen_url} nombre={l.cliente?.nombre || "?"} size={30} rounded="lg"
          data-testid={`mensajeria-thumb-${l.linea_id}`} />
        <span className="text-[10px] text-amber-400 font-mono">{l.peticion?.codigo}</span>
        {l.peticion?.marca && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300">
            <Tag className="w-2 h-2 inline" /> {l.peticion.marca}
          </span>
        )}
        <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${
          urgente ? "bg-amber-500/30 text-amber-200" : "bg-zinc-800 text-zinc-400"
        }`} title={`Producido desde hace ${dias} días`}>
          <Clock className="w-2 h-2 inline" /> {dias}d listo
        </span>
      </div>
      <div className="text-sm font-semibold text-zinc-100 line-clamp-2">{l.descripcion || l.peticion?.nombre}</div>
      <div className="space-y-0.5 text-[11px] text-zinc-400 mt-2">
        <div className="flex items-center gap-1">
          <Building2 className="w-3 h-3 text-zinc-500" /> {l.cliente?.nombre || "—"}
        </div>
        <div className="flex items-center gap-1">
          <MapPin className="w-3 h-3 text-zinc-500" /> {l.espacio?.nombre} · {l.espacio?.ciudad}
        </div>
        <div className="text-[10px] text-zinc-600">
          📦 {l.n_bultos} bulto{l.n_bultos !== 1 ? "s" : ""}
        </div>
        {fase === "esperando" && l.transportista && (
          <div className="mt-1.5 p-1.5 bg-amber-500/10 border border-amber-500/30 rounded text-[10px]">
            <div className="text-amber-200 font-bold">🚚 {l.transportista}</div>
            {l.tracking_envio && <div className="text-amber-300/80 font-mono break-all">{l.tracking_envio}</div>}
            {l.fecha_llamada_mensajeria && <div className="text-amber-300/60 mt-0.5">Llamada: {l.fecha_llamada_mensajeria}</div>}
          </div>
        )}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-1.5">
        <Button size="sm" variant="outline" onClick={() => onImprimir(l.linea_id)}
          data-testid={`btn-etiqueta-msj-${l.linea_id}`}
          className="bg-transparent border-zinc-700 text-zinc-300 text-xs">
          <Printer className="w-3.5 h-3.5 mr-1" /> Etiqueta
        </Button>
        {fase === "pendiente" ? (
          <Button size="sm" onClick={onAccion}
            data-testid={`btn-llamar-${l.linea_id}`}
            className="bg-fuchsia-500 hover:bg-fuchsia-400 text-zinc-950 font-semibold text-xs">
            📞 Llamar agencia
          </Button>
        ) : (
          <Button size="sm" onClick={onAccion}
            data-testid={`btn-recogido-${l.linea_id}`}
            className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold text-xs">
            ✅ Recogido
          </Button>
        )}
      </div>
    </div>
  );
}

function DialogLlamarAgencia({ linea, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [agencia, setAgencia] = useState("");
  const [agenciaCustom, setAgenciaCustom] = useState("");
  const [tracking, setTracking] = useState("");
  const [fecha, setFecha] = useState(today);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const transportistaFinal = agencia === "Otro" ? agenciaCustom.trim() : agencia;
    if (!transportistaFinal) {
      toast.error("Selecciona la agencia");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/lineas/${linea.linea_id}/llamar-mensajeria`, {
        transportista: transportistaFinal,
        tracking_envio: tracking || null,
        fecha_llamada_mensajeria: fecha,
      });
      toast.success("Agencia anotada · esperando recogida");
      onDone();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="dialog-llamar">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            📞 Llamada a la agencia
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-400">
            Anota qué agencia recoge el paquete y, si te dan el nº de seguimiento, regístralo.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2 text-xs">
            <div className="font-semibold text-zinc-100">{linea.descripcion || linea.peticion?.nombre}</div>
            <div className="text-zinc-500 mt-0.5">
              {linea.peticion?.codigo} · {linea.cliente?.nombre} · {linea.n_bultos} bulto{linea.n_bultos !== 1 ? "s" : ""}
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Agencia</label>
            <div className="grid grid-cols-3 gap-1.5">
              {AGENCIAS.map((a) => (
                <button key={a} type="button"
                  onClick={() => setAgencia(a)}
                  data-testid={`agencia-${a}`}
                  className={`px-2 py-1.5 rounded text-xs border font-semibold transition-colors ${
                    agencia === a
                      ? "bg-fuchsia-500/30 border-fuchsia-500 text-fuchsia-100"
                      : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700"
                  }`}>
                  {a}
                </button>
              ))}
            </div>
            {agencia === "Otro" && (
              <Input value={agenciaCustom} onChange={(e) => setAgenciaCustom(e.target.value)}
                placeholder="Nombre agencia"
                data-testid="agencia-custom"
                className="mt-2 bg-zinc-900 border-zinc-800 text-zinc-100" />
            )}
          </div>
          <div>
            <label className="text-xs text-zinc-400">Nº de seguimiento (opcional)</label>
            <Input value={tracking} onChange={(e) => setTracking(e.target.value)}
              placeholder="GLS123456789…"
              data-testid="tracking-input"
              className="bg-zinc-900 border-zinc-800 text-zinc-100" />
          </div>
          <div>
            <label className="text-xs text-zinc-400">Fecha de la llamada</label>
            <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)}
              data-testid="fecha-llamada"
              className="bg-zinc-900 border-zinc-800 text-zinc-100" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancelar</Button>
          <Button onClick={submit} disabled={submitting || !agencia}
            data-testid="llamar-confirmar"
            className="bg-fuchsia-500 hover:bg-fuchsia-400 text-zinc-950 font-semibold">
            {submitting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Guardando…</> :
              <>Guardar llamada</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

